import {
  type ColumnDef,
  type SortingState,
  type Table,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { t } from "i18next";
import * as React from "react";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import type { LineItem } from "@/apis/gql/types";
import { AccountsCell } from "@/components/shared/cells/accounts";
import { AmountField } from "@/components/shared/cells/amount-cell";
import { VatField } from "@/components/shared/cells/vat-rate";
import type { FormValues as AccountFormValues } from "@/components/shared/form/lineItems/account";
import { ValidationErrorBorder } from "@/components/shared/form/shared/form-validation";
import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import { SpecialAccountBU } from "@/components/shared/tables/automatic-account-bu";
import useMultipleSelect from "@/components/shared/tables/multiSelect";
import {
  ComboBox,
  type ComboOption,
} from "@/components/shared/tables/tableComboBox";
import useTableShortcuts from "@/components/shared/tables/tableShortCuts";
import {
  AssistantToolbar,
  AssistantToolbarAskAi,
  AssistantToolbarRoot,
  AssistantToolbarTrigger,
} from "@/components/shared/toolbars/assistant-toolbar";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { confColor } from "@/lib/miscellaneous/confidence-color";
import { serviceResearchPrompt } from "@/lib/prompts/service";
import { cleanVatPreSave, cn } from "@/lib/utils";
import { useGetCompanyInfo } from "@/queries/companies-info/fetch";
import { useGetAllCostCenters } from "@/queries/cost-centers/fetch";
import { useUpdateManyLineItems } from "@/queries/line-items/update";
import { useGetAllTaxCodes } from "@/queries/tax-codes/get-all";
import {
  UpdateInvoiceFields,
  useInvoiceReviewContext,
  useInvoiceReviewStateDispatch,
  useSaveInvoice,
} from "../../ReviewProvider";
import { InvoiceModes } from "../SplitInvoiceForm";

const LineItemTableInstance = React.createContext<
  | {
      table: Table<any>;
      setRowSelection: (value: any) => void;
      rowSelection: any;
    }
  | undefined
>(undefined);

export function useLineItemTableInstance() {
  const context = React.useContext(LineItemTableInstance);
  if (context === undefined) {
    throw new Error(
      "useLineItemTableInstance must be within LineItemTableInstanceProvider",
    );
  }

  return context;
}

type Props = React.PropsWithChildren<{
  data: any;
  columnVisibility: Record<string, boolean>;
  invoiceMode: InvoiceModes;
  readOnly?: boolean;
}>;

export const LineItemTableInstanceProvider = forwardRef(
  (
    { data, children, invoiceMode, columnVisibility, readOnly = false }: Props,
    ref,
  ) => {
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [rowSelection, setRowSelection] = React.useState({});

    // ********  Column Definitions ********
    const columns: ColumnDef<any>[] = useMemo(
      () => [
        {
          id: "select",
          header: ({ table }) => (
            <Checkbox
              checked={
                table.getIsAllPageRowsSelected() ||
                (table.getIsSomeRowsSelected() && "indeterminate")
              }
              onCheckedChange={(value) => {
                table.toggleAllRowsSelected(!!value);
              }}
              aria-label="Select all"
            />
          ),
          cell: ({ row }) => (
            <div onClick={(event) => event.stopPropagation()}>
              <Checkbox
                checked={row.getIsSelected()}
                onChange={(value: any) => row.toggleSelected(!!value)}
                onClick={(event: any) => {
                  if (event.shiftKey) {
                    // should refector this: Problem is that the checkbox does not pass event and value to same handler,
                    // so built this custom approach and call two funciotn, one in on click and other in on checked change
                    setShiftDown(true);
                  }
                }}
                onCheckedChange={(value: any) => {
                  row.toggleSelected(!!value);
                  setCurrentSelectValue({ row, value });
                }}
                aria-label="Select row"
              />
            </div>
          ),
          enableSorting: false,
          enableHiding: false,
        },
        {
          accessorKey: "nettoAmount",
          id: "nettoAmount",
          size: 400,
          minSize: 100,
          enableResizing: false,
          header: ({ column }) => (
            <DataTableColumnHeader
              column={column}
              title="Netto"
              buttonClassName={`m-0 p-1${
                invoiceMode !== InvoiceModes.Netto ? " text-gray-400" : ""
              }`}
            />
          ),
          cell: ({ row }) => {
            const dispatch = useInvoiceReviewStateDispatch();
            const { saveInvoice } = useSaveInvoice();

            const lineItem = row.original.lineItem;
            const onAmountBlur = async (value: any) => {
              if (value === row.original.nettoAmount) {
                return;
              }
              dispatch({
                type: UpdateInvoiceFields.NettoAmount,
                payload: value,
                ids: [lineItem.lineItemId],
              });

              saveInvoice(
                UpdateInvoiceFields.NettoAmount,
                {
                  nettoAmount: value,
                },
                [lineItem.lineItemId],
              );
            };
            return (
              <AmountField
                key={row.id}
                id={row.id}
                invoiceMode={invoiceMode}
                amount={lineItem.nettoAmount}
                amountConfidence={lineItem.amountConfidence}
                fieldType={InvoiceModes.Netto}
                onBlur={onAmountBlur}
                readOnly={readOnly}
              />
            );
          },
        },
        {
          accessorKey: "amount",
          id: "amount",
          size: 400,
          minSize: 100,
          enableResizing: false,
          header: ({ column }) => (
            <DataTableColumnHeader
              column={column}
              title="Brutto"
              buttonClassName={`m-0 p-1${
                invoiceMode !== InvoiceModes.Brutto ? " text-gray-400" : ""
              }`}
            />
          ),
          cell: ({ row }) => {
            const { invoiceReviewState } = useInvoiceReviewContext();
            const { invoiceData } = invoiceReviewState;

            const dispatch = useInvoiceReviewStateDispatch();
            const { saveInvoice } = useSaveInvoice();

            const lineItem = row.original.lineItem;
            const onAmountBlur = async (value: any) => {
              if (value === row.original.lineItem.amount) {
                return;
              }

              const updatedFields = {
                amount: value,
              };

              dispatch({
                type: UpdateInvoiceFields.Amount,
                payload: value,
                ids: [lineItem.lineItemId],
              });

              saveInvoice(UpdateInvoiceFields.Amount, updatedFields, [
                lineItem.lineItemId,
              ]);
            };

            return (
              <AmountField
                key={row.id}
                id={row.id}
                invoiceMode={invoiceMode}
                amount={lineItem.amount}
                amountConfidence={lineItem.amountConfidence}
                fieldType={InvoiceModes.Brutto}
                onBlur={onAmountBlur}
                readOnly={readOnly}
              />
            );
          },
        },
        {
          accessorKey: "vatRate",
          id: "vatRate",
          size: 30,
          enableResizing: true,
          header: ({ column }) => (
            <DataTableColumnHeader
              column={column}
              title="USt."
              buttonClassName="m-0 p-1"
            />
          ),
          cell: ({ row }) => {
            const dispatch = useInvoiceReviewStateDispatch();
            const { saveInvoice } = useSaveInvoice();

            const handleOnChange = async (value: any) => {
              if (value === row.original.vatRate) {
                return;
              }

              dispatch({
                type: UpdateInvoiceFields.VatRate,
                payload: value,
                ids: [row.original.lineItemId],
              });

              saveInvoice(UpdateInvoiceFields.VatRate, { vatRate: value }, [
                row.original.lineItemId,
              ]);
            };

            return (
              <VatField
                key={row.id}
                value={row.original.lineItem.vatRate}
                confidence={row.original.lineItem.vatRateConfidence}
                onChange={handleOnChange}
                readOnly={readOnly}
              />
            );
          },
        },
        {
          accessorKey: "taxCodeId",
          id: "taxCodeId",
          size: 2000,
          minSize: 2000,
          enableResizing: false,
          header: ({ column }) => (
            <DataTableColumnHeader
              column={column}
              title="BU"
              buttonClassName="m-0 p-1"
            />
          ),

          sortingFn: (a, b) => {
            const aName = a.original?.taxCode?.name ?? "";
            const bName = b.original?.taxCode?.name ?? "";

            if (aName === "" && bName === "") {
              return 0;
            } else if (aName === "") {
              return 1;
            } else if (bName === "") {
              return -1;
            } else {
              return aName.localeCompare(bName);
            }
          },
          cell: ({ row }) => {
            const { taxCodes } = useGetAllTaxCodes();
            const dispatch = useInvoiceReviewStateDispatch();
            const { saveInvoice } = useSaveInvoice();

            const taxCodeOptions = taxCodes?.map(
              (taxCode): ComboOption => ({
                label: taxCode.name,
                alternativeLabels: [taxCode.datevBu],
                value: taxCode.taxCodeId,
                subLabel: taxCode.datevBu,
              }),
            );
            const lineItem = row.original;
            const onTaxChange = async (value: any) => {
              if (value == row.original?.taxCode?.taxCodeId) {
                return;
              }
              const selectedTaxCode = taxCodes?.find(
                (taxCode) => taxCode.taxCodeId === value,
              );
              const updatedFields = {
                taxCodeId: value,
              };
              dispatch({
                type: UpdateInvoiceFields.TaxCode,
                payload: selectedTaxCode,
                ids: [lineItem.lineItemId],
              });
              saveInvoice(UpdateInvoiceFields.TaxCodeId, updatedFields, [
                lineItem.lineItemId,
              ]);

              const dispatchAndSaveVatRate = async (
                vatRate: number | string | undefined,
                ids: string[],
              ) => {
                const cleanedVatRate = cleanVatPreSave(vatRate);

                dispatch({
                  type: UpdateInvoiceFields.VatRate,
                  payload: cleanedVatRate,
                  ids,
                });

                await saveInvoice(
                  UpdateInvoiceFields.VatRate,
                  { vatRate: cleanedVatRate },
                  ids,
                );
              };

              dispatchAndSaveVatRate(selectedTaxCode?.vatRate, [
                lineItem.lineItemId,
              ]);
            };

            const taxCode = lineItem?.taxCode;
            const taxCodeName = taxCode ? taxCode.name : "Steuerschlüssel";
            const currentOption = {
              label: taxCodeName,
              alternativeLabels: [taxCode?.datevBu],
              value: taxCode?.taxCodeId,
              subLabel: taxCode?.datevBu || "Unbekannt",
            };

            return (
              <div className="m-1">
                {row.original?.expenseAccount?.automatic ? (
                  <SpecialAccountBU
                    label="Automatischer"
                    subLabel="Steuerschlüssel"
                    className={cn(confColor(row.original?.taxCodeConfidence))}
                  />
                ) : row.original?.expenseAccount?.buExport === false ? (
                  <SpecialAccountBU label="Konto ohne BU" />
                ) : (
                  <ComboBox
                    currentOption={currentOption}
                    onChange={onTaxChange}
                    items={taxCodeOptions ?? []}
                    placeholder="Steuerschlüssel"
                    fullWidth={false}
                    emptyLabel="Keine BU gefunden"
                    className={cn(confColor(row.original?.taxCodeConfidence))}
                    readOnly={readOnly}
                  />
                )}
              </div>
            );
          },
        },
        {
          accessorKey: "accountId",
          id: "expenseAccount",
          size: 2000,
          minSize: 2000,
          enableResizing: false,

          header: ({ column }) => (
            <DataTableColumnHeader
              column={column}
              title="Konto"
              buttonClassName="m-0 p-1"
            />
          ),

          sortingFn: (a, b) => {
            const aName = a.original?.expenseAccount?.accountName ?? "";
            const bName = b.original?.expenseAccount?.accountName ?? "";

            if (aName === "" && bName === "") {
              return 0;
            } else if (aName === "") {
              return 1;
            } else if (bName === "") {
              return -1;
            } else {
              return aName.localeCompare(bName);
            }
          },
          cell: ({ row }) => {
            const lineItemId = row.original.lineItemId;
            const accountId = row.original.expenseAccount?.accountId;
            const { refetchLineItems } = useInvoiceReviewContext();
            const updateLineItems = useUpdateManyLineItems();

            const handleOnChange = async (value: AccountFormValues) => {
              await updateLineItems(value.lineItemIds, {
                accountId: value.accountId,
              });
            };

            return (
              <ValidationErrorBorder
                name="accountId"
                className="m-1 before:rounded-md before:ring-1 before:ring-destructive"
              >
                <AccountsCell
                  lineItemIds={[lineItemId]}
                  accountIds={[accountId]}
                  onAccountChange={handleOnChange}
                  className={cn(confColor(row.original?.accountConfidence))}
                  onSuccess={() => refetchLineItems()}
                  readOnly={readOnly}
                />
              </ValidationErrorBorder>
            );
          },
        },
        {
          accessorKey: "costCenterId",
          id: "costCenterId",
          size: 2000,
          minSize: 2000,
          enableResizing: false,

          header: ({ column }) => (
            <DataTableColumnHeader
              column={column}
              title="Kostenstelle"
              buttonClassName="m-0 p-1"
            />
          ),

          sortingFn: (a, b) => {
            const aName = a.original?.costCenter?.name ?? "";
            const bName = b.original?.costCenter?.name ?? "";

            if (aName === "" && bName === "") {
              return 0;
            } else if (aName === "") {
              return 1;
            } else if (bName === "") {
              return -1;
            } else {
              return aName.localeCompare(bName);
            }
          },
          cell: ({ row }) => {
            const dispatch = useInvoiceReviewStateDispatch();
            const { saveInvoice } = useSaveInvoice();
            const { costCenters } = useGetAllCostCenters();

            const onCostCenterChange = async (value: any) => {
              const lineItemId = row.original.lineItemId;

              const updatedFields = {
                costCenterId: value !== "unknown" ? value : null,
              };

              dispatch({
                type: UpdateInvoiceFields.CostCenterId,
                payload: value,
                ids: [lineItemId],
              });
              saveInvoice(UpdateInvoiceFields.CostCenterId, updatedFields, [
                lineItemId,
              ]);
            };

            const costCenterOptions =
              costCenters?.map(
                (costCenter, index): ComboOption => ({
                  label: costCenter.name,
                  subLabel: costCenter.identifier,
                  alternativeLabels: [],
                  value: costCenter.costCenterId,
                }),
              ) ?? [];
            const options = [
              ...costCenterOptions,
              {
                label: t("cost_centers.no_cost_center"),
                subLabel: "0",
                alternativeLabels: [],
                value: "unknown",
              },
            ];

            const currentCostCenterOption = options.find(
              (option) => option.value === row.original.costCenterId,
            );

            return (
              <div className="m-1">
                <ComboBox
                  currentOption={currentCostCenterOption}
                  onChange={onCostCenterChange}
                  items={options ?? []}
                  emptyLabel={t("cost_centers.no_cost_center")}
                  placeholder={t("cost_centers.cost_center")}
                  fullWidth={false}
                  className={cn(confColor(row.original?.accountConfidence))}
                  readOnly={readOnly}
                />
              </div>
            );
          },
        },
        {
          accessorKey: "name",
          id: "name",
          size: 3000,
          minSize: 3000,
          enableResizing: false,
          filterFn: (row, columnId, value) => {
            return row.original.invoice?.supplier?.supplierId === value;
          },
          header: ({ column }) => (
            <DataTableColumnHeader
              column={column}
              title="Buchungstext"
              buttonClassName="m-0 p-1"
            />
          ),
          cell: ({ row }) => {
            const dispatch = useInvoiceReviewStateDispatch();
            const { saveInvoice } = useSaveInvoice();
            const { companyInfo } = useGetCompanyInfo();
            const { invoiceReviewState } = useInvoiceReviewContext();
            const { invoiceData } = invoiceReviewState;

            const onNameChange = async (value: any) => {
              if (value === row.original.name) {
                return;
              }
              const lineItemId = row.original.lineItemId;
              const updatedFields = {
                name: value,
              };
              dispatch({
                type: UpdateInvoiceFields.Name,
                payload: value,
                ids: [lineItemId],
              });
              saveInvoice(UpdateInvoiceFields.Name, updatedFields, [
                lineItemId,
              ]);
            };

            return (
              <NameInput
                row={row}
                onNameChange={onNameChange}
                readOnly={readOnly}
                companyDescription={
                  companyInfo?.companyDescription ?? "not available"
                }
                invoiceText={invoiceData?.invoiceText ?? ""}
              />
            );
          },
        },
      ],
      [invoiceMode],
    );

    // ********  Table Definition ********

    const indexSort = useCallback((lineItems: LineItem[] | undefined) => {
      if (!lineItems) return [];
      const sorted = [...lineItems].sort(
        (a, b) => (a.index ?? 0) - (b.index ?? 0),
      );
      return sorted;
    }, []);

    const sortedData = useMemo(() => {
      return indexSort(data);
    }, [data, indexSort]);

    const table = useReactTable({
      data: sortedData,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onSortingChange: setSorting,
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      onRowSelectionChange: setRowSelection,
      autoResetPageIndex: false,
      getRowId: (row) => row.lineItem.lineItemId,
      state: {
        sorting,
        pagination: {
          pageIndex: 0,
          pageSize: 1000,
        },
        columnVisibility,
        rowSelection,
      },
    });

    // **************** Shortcuts ****************

    const handleEsc = () => {
      table.resetRowSelection(true);
    };

    useTableShortcuts({
      handleCmdA: () => table.toggleAllRowsSelected(true),
      handleEsc,
    });

    const { setCurrentSelectValue, setShiftDown } = useMultipleSelect(table);

    useImperativeHandle(ref, () => table, [table]);

    return (
      <LineItemTableInstance.Provider
        value={{
          table,
          setRowSelection,
          rowSelection,
        }}
      >
        {children}
      </LineItemTableInstance.Provider>
    );
  },
);

function NameInput({
  row,
  onNameChange,
  companyDescription,
  invoiceText,
  readOnly = false,
}: {
  row: any;
  onNameChange: (value: any) => void;
  companyDescription: string;
  invoiceText: string;
  readOnly?: boolean;
}): any {
  const [currentValue, setCurrentValue] = useState(
    row.original?.lineItem?.name ?? "Unbekannt",
  );

  return (
    <AssistantToolbarRoot>
      <AssistantToolbarTrigger
        className="text-left overflow-hidden whitespace-nowrap text-ellipsis cursor-pointer"
        asChild={false}
      >
        <div className="flex border-0 pl-0 pr-0 m-0 items-center justify-between">
          <Input
            className="overflow-hidden whitespace-nowrap text-ellipsis flex-1 border-0 outline-none pl-1 pr-1 m-0"
            value={currentValue}
            style={{
              border: "none",
              outline: "none",
              backgroundColor: "transparent",
            }}
            readOnly={readOnly}
            onBlur={(e) => onNameChange(e.target.value)}
            onChange={(e) => setCurrentValue(e.target.value)}
          />
        </div>
      </AssistantToolbarTrigger>
      <AssistantToolbar side="right">
        <AssistantToolbarAskAi
          aiPrompt={serviceResearchPrompt(
            currentValue,
            companyDescription,
            invoiceText,
          )}
          label={t("ask_ai.extras.service")}
          query={currentValue}
        />
      </AssistantToolbar>
    </AssistantToolbarRoot>
  );
}
